import React, { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowRightOutlined } from '@ant-design/icons';
import { SEOHead } from '../components/SEO/SEOHead';
import PublicHeader from '../components/landing/PublicHeader';
import PublicFooter from '../components/landing/PublicFooter';
import CtaSection from '../components/landing/CtaSection';
import { articleBySlug } from '../content/articles';
import { articleJsonLd, articleLang, articleUi, bodyToHtml, formatDate, inline, tableOfContents } from '../content/render';
import type { Article } from '../content/types';
import '../components/landing/landing.css';
import './Blog.css';

const BlogArticle: React.FC = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const article = articleBySlug(slug);

  const html = useMemo(() => (article ? bodyToHtml(article.body) : ''), [article]);
  const toc = useMemo(() => (article ? tableOfContents(article) : []), [article]);

  useEffect(() => {
    if (!window.location.hash) window.scrollTo({ top: 0 });
  }, [slug]);

  // Liens internes du HTML rendu → navigation SPA
  const onBodyClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = (e.target as HTMLElement).closest('a');
    if (!target) return;
    const href = target.getAttribute('href') || '';
    if (href.startsWith('/') && !href.startsWith('//')) {
      e.preventDefault();
      navigate(href);
    }
  };

  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  const related = article.relatedSlugs.map(articleBySlug).filter((a): a is Article => !!a);
  const lang = articleLang(article);
  const ui = articleUi(lang);
  const dateLocale = lang === 'fr' ? 'fr-FR' : 'en-US';

  return (
    <>
      <SEOHead
        title={article.metaTitle}
        description={article.metaDescription}
        canonical={`/blog/${article.slug}/`}
        keywords={article.keywords.join(', ')}
        ogType="article"
        lang={lang}
      />
      <Helmet>
        <meta property="article:published_time" content={article.datePublished} />
        <meta property="article:modified_time" content={article.dateModified} />
        <meta property="article:section" content={article.category} />
        {article.keywords.map((k) => (
          <meta key={k} property="article:tag" content={k} />
        ))}
        <script type="application/ld+json">{JSON.stringify(articleJsonLd(article))}</script>
      </Helmet>

      <PublicHeader />

      <div className="ts-landing blog-page">
        <article className="article">
          <header className="article-hero">
            <div className="ts-blob ts-blob--peach article-blob-1" />
            <div className="ts-blob ts-blob--lilac article-blob-2" />
            <div className="article-container">
              <nav className="blog-breadcrumb" aria-label="Breadcrumb">
                <Link to="/">{ui.home}</Link> <span>/</span> <Link to="/blog">Blog</Link> <span>/</span>{' '}
                <span>{article.category}</span>
              </nav>
              <h1 className="article-title">{article.title}</h1>
              <p className="article-lede">{article.excerpt}</p>
              <div className="article-meta">
                <span className="blog-card-category">{article.category}</span>
                <span>
                  {ui.published} <time dateTime={article.datePublished}>{formatDate(article.datePublished, dateLocale)}</time>
                </span>
                {article.dateModified !== article.datePublished && (
                  <span>
                    {ui.updated} <time dateTime={article.dateModified}>{formatDate(article.dateModified, dateLocale)}</time>
                  </span>
                )}
                <span>
                  {article.readingTimeMin} {ui.minRead}
                </span>
              </div>
            </div>
          </header>

          <div className="article-container article-layout">
            <aside className="article-sidebar">
              <div className="article-toc">
                <h2>{ui.inThisArticle}</h2>
                <ul>
                  {toc.map((h) => (
                    <li key={h.id}>
                      <a href={`#${h.id}`}>{h.text}</a>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="article-side-cta">
                <h3>{ui.sideTitle}</h3>
                <p>{ui.sideText}</p>
                <Link to="/login" className="ts-btn ts-btn--primary">
                  {ui.sideCta} <ArrowRightOutlined />
                </Link>
              </div>
            </aside>

            <div className="article-main">
              <section className="article-takeaways" aria-label={ui.takeaways}>
                <h2>{ui.takeaways}</h2>
                <ul>
                  {article.keyTakeaways.map((k, i) => (
                    <li key={i} dangerouslySetInnerHTML={{ __html: inline(k) }} />
                  ))}
                </ul>
              </section>

              <div className="article-body" onClick={onBodyClick} dangerouslySetInnerHTML={{ __html: html }} />

              {article.faq.length > 0 && (
                <section className="article-faq" aria-labelledby="faq-title">
                  <h2 id="faq-title">{ui.faq}</h2>
                  {article.faq.map((f, i) => (
                    <details key={i} className="article-faq-item" open={i === 0}>
                      <summary>{f.q}</summary>
                      <p>{f.a}</p>
                    </details>
                  ))}
                </section>
              )}
            </div>
          </div>

          {related.length > 0 && (
            <section className="article-related">
              <div className="ts-container">
                <h2 className="article-related-title">{ui.related}</h2>
                <div className="blog-grid">
                  {related.map((a) => (
                    <Link key={a.slug} to={`/blog/${a.slug}`} className="blog-card">
                      <div className="blog-card-meta">
                        <span className="blog-card-category">{a.category}</span>
                        <span>
                          {a.readingTimeMin} {ui.minRead}
                        </span>
                      </div>
                      <h3 className="blog-card-title">{a.title}</h3>
                      <p className="blog-card-excerpt">{a.metaDescription}</p>
                      <span className="blog-card-cta">{ui.readMore} →</span>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </article>

        <CtaSection />
        <PublicFooter />
      </div>
    </>
  );
};

export default BlogArticle;
